import { db } from "../lib/firebase";
import { collection, getDocs, query, where, orderBy, addDoc, serverTimestamp, onSnapshot, doc, runTransaction, getDoc, setDoc } from "firebase/firestore";
import type { CartItem as CartItemSvc } from "./cart";
import { holdInEscrow } from '../services/wallet';
import { notifyAdmins } from './email';

export interface OrderItemRef {
  listingId?: string;
  title?: string;
  price?: number;
  image?: string;
  quantity?: number;
}

export interface Order {
  id?: string;
  buyerId: string;
  sellerId: string;
  status: "payment_held" | "delivery_started" | "delivered" | "completed" | "cancelled";
  totalAmount: number;
  createdAt?: any; // Firestore Timestamp
  orderItems?: OrderItemRef[];
  orderId?: string; // optional human-friendly id
  paymentReference?: string;
  // Buyer-provided order details
  buyerName?: string;
  buyerPhone?: string;
  buyerEmail?: string;
  deliveryAddress?: string;
  shareLocation?: boolean;
  buyerNote?: string;
  coordinates?: { lat?: number; lng?: number };
}

// Helper: deeply remove undefined values to satisfy Firestore
const cleanUndefined = (val: any): any => {
  if (Array.isArray(val)) return val.map(v => cleanUndefined(v));
  if (val && typeof val === 'object') {
    const out: any = {};
    Object.keys(val).forEach((k) => {
      const v = (val as any)[k];
      if (v !== undefined) out[k] = cleanUndefined(v);
    });
    return out;
  }
  return val;
};

export async function getBuyerOrders(uid: string): Promise<Order[]> {
  try {
    const q = query(collection(db, "orders"), where("buyerId", "==", uid), orderBy("createdAt", "desc"));
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...(d.data() as Order) }));
  } catch (e: any) {
    // Graceful fallback while composite index builds
    const msg = String(e?.message || "").toLowerCase();
    if (e?.code === "failed-precondition" || msg.includes("requires an index")) {
      const qNoOrder = query(collection(db, "orders"), where("buyerId", "==", uid));
      const snap = await getDocs(qNoOrder);
      const items = snap.docs.map(d => ({ id: d.id, ...(d.data() as Order) }));
      items.sort((a, b) => ((b as any).createdAt?.seconds || 0) - ((a as any).createdAt?.seconds || 0));
      return items;
    }
    throw e;
  }
}

export function subscribeUserOrders(uid: string, callback: (orders: Order[]) => void, onlyActive: boolean = false) {
  const baseQ = query(collection(db, "orders"), where("buyerId", "==", uid));
  const qFinal = baseQ; // optional filter could be applied when onlyActive is true
  return onSnapshot(qFinal, (snap) => {
    let items = snap.docs.map(d => ({ id: d.id, ...(d.data() as Order) }));
    if (onlyActive) {
      items = items.filter(o => ["payment_held", "delivery_started", "delivered"].includes(o.status));
    }
    items.sort((a, b) => ((b as any).createdAt?.seconds || 0) - ((a as any).createdAt?.seconds || 0));
    callback(items);
  });
}

export async function createOrdersSplitBySeller(params: {
  buyerId: string;
  cartItems: CartItemSvc[];
  paymentReference?: string;
  buyerDetails?: {
    buyerName?: string;
    buyerPhone?: string;
    buyerEmail?: string;
    deliveryAddress?: string;
    shareLocation?: boolean;
    buyerNote?: string;
    coordinates?: { lat?: number; lng?: number };
  };
}): Promise<{ orderIds: string[]; ordersBySeller: { sellerId: string; orderId: string }[] }> {
  const { buyerId, cartItems, paymentReference, buyerDetails } = params;

  // Idempotency guard by paymentReference
  if (paymentReference) {
    const idemRef = doc(db, 'order_idempotency', `${buyerId}:${paymentReference}`);
    const existing = await getDoc(idemRef);
    if (existing.exists()) {
      const data = existing.data() as any;
      if (data.status === 'completed' && Array.isArray(data.orderIds)) {
        return { orderIds: data.orderIds as string[], ordersBySeller: (data.ordersBySeller || []).map((x: any) => ({ sellerId: x.sellerId, orderId: x.orderId })) };
      }
      if (data.status === 'pending') {
        throw new Error('ORDER_CREATION_IN_PROGRESS');
      }
    }
    // Atomically create a pending idempotency marker
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(idemRef);
      if (snap.exists()) {
        return; // someone else created it
      }
      tx.set(idemRef, { buyerId, paymentReference, status: 'pending', createdAt: serverTimestamp() } as any);
    });
  }

  // Fetch buyer name for richer transaction descriptions
  let buyerNameForDesc: string | null = null;
  try {
    const buyerSnap = await getDoc(doc(db, 'users', buyerId));
    if (buyerSnap.exists()) {
      const bd = buyerSnap.data() as any;
      buyerNameForDesc = bd.fullName || bd.email || null;
    }
  } catch {}

  const groups = cartItems.reduce((acc, item) => {
    const key = item.sellerId || "unknown";
    if (!acc[key]) acc[key] = [] as CartItemSvc[];
    acc[key].push(item);
    return acc;
  }, {} as Record<string, CartItemSvc[]>);

  const orderIds: string[] = [];
  const ordersBySeller: { sellerId: string; orderId: string }[] = [];

  for (const [sellerId, items] of Object.entries(groups)) {
    const totalAmount = items.reduce((sum, i) => sum + i.price * (i.quantity || 1), 0);
    const orderDoc = {
      buyerId,
      sellerId,
      status: "payment_held" as const,
      totalAmount,
      orderItems: items.map(i => ({ listingId: i.listingId, title: i.title, price: i.price, image: i.image, quantity: i.quantity })),
      createdAt: serverTimestamp(),
      paymentReference,
      buyerName: buyerDetails?.buyerName,
      buyerPhone: buyerDetails?.buyerPhone,
      buyerEmail: buyerDetails?.buyerEmail,
      deliveryAddress: buyerDetails?.deliveryAddress,
      shareLocation: buyerDetails?.shareLocation,
      buyerNote: buyerDetails?.buyerNote,
      coordinates: buyerDetails?.coordinates,
    } as Order & { createdAt: any };

    const sanitized = cleanUndefined(orderDoc);
    const added = await addDoc(collection(db, "orders"), sanitized);
    orderIds.push(added.id);
    ordersBySeller.push({ sellerId, orderId: added.id });

    await holdInEscrow(sellerId, totalAmount, added.id);
  }

  // After all orders are created, update buyer's reserved balance
  await runTransaction(db, async (t) => {
    const buyerWalletRef = doc(db, "wallets", buyerId);
    const snap = await t.get(buyerWalletRef);
    const current = snap.exists() ? (snap.data() as { balance?: number; escrowBalance?: number; totalEarnings?: number; reservedBalance?: number }) : { balance: 0, escrowBalance: 0, totalEarnings: 0, reservedBalance: 0 };
    const totalCartAmount = cartItems.reduce((sum, i) => sum + i.price * (i.quantity || 1), 0);
    const updated = { ...current, reservedBalance: (current.reservedBalance || 0) + totalCartAmount };
    t.set(buyerWalletRef, updated, { merge: true });
  });

  // Also record purchase in buyer's wallet history for visibility
  try {
    const buyerWalletRef = doc(db, "wallets", buyerId);
    const totalCartAmount = cartItems.reduce((sum, i) => sum + i.price * (i.quantity || 1), 0);
    const txPayload = {
      type: "debit",
      amount: totalCartAmount,
      description: `Purchase for orders ${orderIds.join(', ')} (held in escrow)`,
      status: "held",
      orderId: orderIds.join(','), // Store all order IDs
      reference: paymentReference,
      method: "internal",
      createdAt: serverTimestamp(),
    } as any;
    await addDoc(collection(buyerWalletRef, "transactions"), cleanUndefined(txPayload));
  } catch {}

  // Mark idempotency record completed
  if (paymentReference) {
    const idemRef = doc(db, 'order_idempotency', `${buyerId}:${paymentReference}`);
    try {
      await setDoc(idemRef, { status: 'completed', orderIds, ordersBySeller, completedAt: serverTimestamp() } as any, { merge: true });
    } catch {}
  }

  // Notify admins of the new transaction(s) (fire-and-forget)
  const totalCartAmount = cartItems.reduce((sum, i) => sum + i.price * (i.quantity || 1), 0);
  notifyAdmins({
    event: 'new_order',
    subject: `New transaction: ₦${totalCartAmount.toLocaleString()} (${orderIds.length} order${orderIds.length === 1 ? '' : 's'})`,
    text: `A buyer just completed a purchase on Campus Market.`,
    meta: {
      buyerId,
      buyerName: buyerNameForDesc || buyerDetails?.buyerName || '',
      buyerEmail: buyerDetails?.buyerEmail || '',
      totalAmount: totalCartAmount,
      orderIds: orderIds.join(', '),
      sellers: ordersBySeller.map(o => o.sellerId).join(', '),
      paymentReference: paymentReference || '',
      itemCount: cartItems.length,
    },
  });

  return { orderIds, ordersBySeller };
}

export async function getOrdersCountForSeller(sellerId: string): Promise<number> {
  const q = query(collection(db, "orders"), where("sellerId", "==", sellerId));
  const snap = await getDocs(q);
  return snap.size;
}

export async function getCompletedOrdersCountForSeller(sellerId: string): Promise<number> {
  const q = query(
    collection(db, "orders"),
    where("sellerId", "==", sellerId),
    where("status", "==", "completed")
  );
  const snap = await getDocs(q);
  return snap.size;
}